import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { isLikelyBot } from '../functions/bot-detector.function';
import { IScriptLoadOptions, IScriptLoadTrigger } from '../models/script-loader.model';

const USER_INTERACTION_EVENTS: readonly (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

@Injectable({ providedIn: 'root' })
export class ScriptLoaderService {
    readonly #doc: Document = inject(DOCUMENT);
    readonly #platformId: object = inject(PLATFORM_ID);

    readonly #inFlight: Map<string, Promise<void>> = new Map<string, Promise<void>>();

    /**
     * Load a script immediately. Idempotent: repeated calls with the same `id`
     * return the same Promise, and a pre-existing <script id="…"> tag in the
     * DOM is treated as already loaded. SSR returns a resolved Promise.
     */
    public load(options: IScriptLoadOptions, skipForBots: boolean = false): Promise<void> {
        if (!isPlatformBrowser(this.#platformId)) {
            return Promise.resolve();
        }
        if (skipForBots && this.#isBot()) {
            return Promise.resolve();
        }

        const existing: Promise<void> | undefined = this.#inFlight.get(options.id);
        if (existing) {
            return existing;
        }

        if (this.#doc.getElementById(options.id)) {
            const resolved: Promise<void> = Promise.resolve();
            this.#inFlight.set(options.id, resolved);
            return resolved;
        }

        const promise: Promise<void> = new Promise<void>((resolve: () => void, reject: (reason: Error) => void): void => {
            const script: HTMLScriptElement = this.#doc.createElement('script');
            script.id = options.id;
            script.src = options.src;
            script.async = options.async ?? true;
            if (options.defer) {
                script.defer = true;
            }
            if (options.attributes) {
                for (const [key, value] of Object.entries(options.attributes)) {
                    script.setAttribute(key, value);
                }
            }
            script.onload = (): void => {
                resolve();
            };
            script.onerror = (): void => {
                reject(new Error(`ScriptLoaderService: failed to load ${options.src}`));
            };

            const parent: HTMLElement = options.target === 'body' ? this.#doc.body : this.#doc.head;
            parent.appendChild(script);
        });

        this.#inFlight.set(options.id, promise);
        return promise;
    }

    /**
     * Load a script lazily, deferred until any of the provided triggers fires.
     * Multiple triggers race — first to fire wins, the rest are unregistered.
     * Semantics match our historical inline loader in index.html.
     */
    public loadDeferred(options: IScriptLoadOptions, triggers: readonly IScriptLoadTrigger[], skipForBots: boolean = false): Promise<void> {
        if (!isPlatformBrowser(this.#platformId)) {
            return Promise.resolve();
        }
        if (skipForBots && this.#isBot()) {
            return Promise.resolve();
        }

        const existing: Promise<void> | undefined = this.#inFlight.get(options.id);
        if (existing) {
            return existing;
        }

        const windowRef: Window | null = this.#doc.defaultView;
        if (!windowRef) {
            return Promise.resolve();
        }

        const promise: Promise<void> = new Promise<void>((resolve: () => void, reject: (reason: Error) => void): void => {
            let started: boolean = false;
            const cleanupFns: (() => void)[] = [];

            const start: () => void = (): void => {
                if (started) {
                    return;
                }
                started = true;
                for (const fn of cleanupFns) {
                    fn();
                }
                // Drop this deferred placeholder before delegating to load(): otherwise
                // load() sees our own promise in #inFlight, treats the script as already
                // loading, and never injects the <script> — so the tracker never loads.
                this.#inFlight.delete(options.id);
                this.load(options).then(resolve, reject);
            };

            for (const trigger of triggers) {
                this.#installTrigger(windowRef, trigger, start, cleanupFns);
            }
        });

        this.#inFlight.set(options.id, promise);
        return promise;
    }

    #installTrigger(windowRef: Window, trigger: IScriptLoadTrigger, start: () => void, cleanupFns: (() => void)[]): void {
        if (trigger.type === 'user-interaction') {
            const handler: () => void = (): void => {
                start();
            };
            for (const eventName of USER_INTERACTION_EVENTS) {
                windowRef.addEventListener(eventName, handler, { once: true, passive: true });
                cleanupFns.push((): void => {
                    windowRef.removeEventListener(eventName, handler);
                });
            }
            return;
        }

        if (trigger.type === 'idle') {
            if ('requestIdleCallback' in windowRef) {
                const handle: number = windowRef.requestIdleCallback(start, { timeout: trigger.timeoutMs });
                cleanupFns.push((): void => {
                    (windowRef as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(handle);
                });
            } else {
                // legacy fallback — treat idle trigger as timeout
                const handle: ReturnType<typeof setTimeout> = setTimeout(start, trigger.timeoutMs);
                cleanupFns.push((): void => {
                    clearTimeout(handle);
                });
            }
            return;
        }

        {
            // Остался единственный вид триггера — по таймеру.
            const handle: ReturnType<typeof setTimeout> = setTimeout(start, trigger.delayMs);
            cleanupFns.push((): void => {
                clearTimeout(handle);
            });
        }
    }

    #isBot(): boolean {
        const windowRef: Window | null = this.#doc.defaultView;
        return windowRef ? isLikelyBot(windowRef) : false;
    }
}
